import React, { useState, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';

import {
  makeStyles,
  FormControl,
  FormHelperText,
  InputLabel,
  FilledInput,
  Typography,
  Link,
  Button,
  Box
} from '@material-ui/core';

import MinimalFormPage from '../layout/MinimalFormPage';
import Alert from '../common/alert';

import validatePassword from './passwordValidation';

import {
  resetPassword,
  selectResetPwStatus,
  clearResetPwState
} from '../auth/authSlice';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    marginTop: theme.spacing(1)
  },
  btn: {
    minWidth: '147px'
  }
}));

const ResetPasswordPage = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { token } = useParams();

  const { status, error } = useSelector(selectResetPwStatus);

  const [ state, setState ] = useState({
    newPassword: '',
    newPasswordConfirm: ''
  });
  const [ validationErrors, setValidationErrors ] = useState({});

  useLayoutEffect(() => {
    return () => {
      dispatch(clearResetPwState());
    }
  }, []);


  const validate = () => {
    const newErrors = {};

    const newPwValidation = validatePassword(state.newPassword);

    if(newPwValidation.error) {
      newErrors.newPassword = newPwValidation.error;
    }

    if(state.newPassword !== state.newPasswordConfirm) {
      newErrors.newPasswordConfirm = t('user:newPasswordsMustMatch');
    }

    setValidationErrors(newErrors);

    if(Object.keys(newErrors).every(field => newErrors[field] === null)) {
      return true;
    }

    return false;
  }

  const onFieldChange = event => {
    setState({
      ...state,
      [event.target.name]: event.target.value
    });

    // Reset errors on this field
    setValidationErrors({
      ...validationErrors,
      [event.target.name]: null
    });

    // Reset possible request error messages
    if(status === 'failed') {
      dispatch(clearResetPwState());
    }
  }


  const onSubmit = event => {
    event.preventDefault();

    if(validate()) {
      if(status !== 'loading') {
        dispatch(resetPassword({newPassword: state.newPassword, newPasswordConfirm: state.newPasswordConfirm, passwordToken: token}));
      }
    }
  };

  return (
    <MinimalFormPage>


      {status === 'failed' ? <Alert severity="error"><Trans t={t} i18nKey={`errors:${error.message}`} components={[<Link key="pwreset" component={RouterLink} to="/forgot-password" />]} /></Alert> : null}
      {status === 'succeeded' ? <Alert severity="success"><Trans t={t} i18nKey='resetPassword:success' components={[<Link key="pwlogin" component={RouterLink} to="/login" />]}/></Alert> : null}

      <form className={classes.form} onSubmit={onSubmit}>
        <FormControl variant="filled" margin="normal" error={!!validationErrors.newPassword}>
          <InputLabel htmlFor="email">{t('resetPassword:newPassword')}</InputLabel>
          <FilledInput
            type="password"
            id="newPassword"
            name="newPassword"
            value={state.newPassword}
            required
            placeholder={t('resetPassword:passwordPlaceholder')}
            autoComplete="none"
            autoFocus
            onChange={onFieldChange}
            aria-describedby={validationErrors.newPassword ? 'password-error-text' : undefined }
          />
          {validationErrors.newPassword ? <FormHelperText id="password-error-text">{validationErrors.newPassword}</FormHelperText> : null}
        </FormControl>

        <FormControl variant="filled" margin="normal" error={!!validationErrors.newPasswordConfirm}>
          <InputLabel htmlFor="email">{t('resetPassword:newPasswordConfirm')}</InputLabel>
          <FilledInput
            type="password"
            id="newPasswordConfirm"
            name="newPasswordConfirm"
            value={state.newPasswordConfirm}
            required
            placeholder={t('resetPassword:passwordPlaceholder')}
            autoComplete="none"
            onChange={onFieldChange}
            aria-describedby={validationErrors.newPasswordConfirm ? 'password-error-text' : undefined }
          />
          {validationErrors.newPasswordConfirm ? <FormHelperText id="password-error-text">{validationErrors.newPasswordConfirm}</FormHelperText> : null}
        </FormControl>

        <Box mt={2} mb={3}>
          <Button className={classes.btn} color="primary" variant="contained" type="submit" disabled={status === 'loading'}>{t('resetPassword:reset')}</Button>
        </Box>
      </form>

      <Typography variant="body1" component="p">
        <Link component={RouterLink} to="/login">{t('forgotPassword:signIn')}</Link>
      </Typography>
    </MinimalFormPage>
  );
};

export default ResetPasswordPage;
